import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Shield, Lock, ArrowRight } from 'lucide-react';
import Card from '../components/UI/Card';
import LoadingSpinner from '../components/UI/LoadingSpinner';

const Auth = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);

  useEffect(() => {
    // Load users data
    const loadUsers = async () => {
      try {
        const response = await fetch('/users.json');
        const userData = await response.json();
        setUsers(userData);
      } catch (err) {
        console.error('Failed to load users data:', err);
      }
    };
    loadUsers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Simulate loading delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Validate code
    const isValidCode = users.some(user => {
      const lastFourDigits = user.phone_number.slice(-4);
      return lastFourDigits === code;
    });

    if (isValidCode) {
      // Find the authenticated user
      const authenticatedUser = users.find(user => {
        const lastFourDigits = user.phone_number.slice(-4);
        return lastFourDigits === code;
      });

      // Store authentication and user info in session
      sessionStorage.setItem('hostbuddy_authenticated', 'true');
      sessionStorage.setItem('hostbuddy_current_user', JSON.stringify(authenticatedUser));
      
      // Get the intended destination from location state or default to calendar
      const from = location.state?.from?.pathname + (location.state?.from?.search || '') || '/calendar';
      navigate(from, { replace: true });
    } else {
      setError(t('auth.invalidCode'));
    }

    setLoading(false);
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 4) {
      setCode(value);
      setError('');
    }
  };

  return (
    <div className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto space-y-8">
        <div className="text-center animate-fade-in">
          <div className="mx-auto w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mb-6">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gradient mb-2">
            {t('auth.title')}
          </h2>
          <p className="text-gray-400">
            {t('auth.subtitle')}
          </p>
        </div>

        <Card glass className="p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-300 mb-2">
                {t('auth.codeLabel')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="code"
                  name="code"
                  type="text"
                  value={code}
                  onChange={handleCodeChange}
                  placeholder={t('auth.codePlaceholder')}
                  className="glass w-full pl-10 pr-4 py-3 border border-white/10 rounded-lg bg-white/5 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  maxLength="4"
                  required
                />
              </div>
              {error && (
                <p className="mt-2 text-sm text-red-400 animate-fade-in">
                  {error}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || code.length !== 4}
              className="w-full flex justify-center items-center space-x-2 py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:shadow-lg"
            >
              {loading ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  <span>{t('auth.verifyButton')}</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        </Card>

        <div className="text-center">
          <p className="text-sm text-gray-400">
            {t('auth.helpContact')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;